import { VerificationMatrix } from "@admin/components/lawyers/VerificationMatrix";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { adminApi } from "@admin/services/adminApi";
import {
  ArrowLeft, CheckCircle2, AlertTriangle, ShieldCheck, Loader2,
  BadgeCheck, FileText, Wallet, User, Scale, Clock, Sticker, MessageSquare,
  Eye, Star, Layers,
} from "lucide-react";
import { PricingLevelCard } from "@admin/components/lawyers/PricingLevelCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { formatLawyerDisplayName } from "@admin/lib/lawyerDisplayName";
import { photoSrc } from "@shared/photoSrc";

/**
 * Single, consolidated Lawyer Verification review workspace.
 *
 * Product principle enforced here: there is ONE review surface for
 * every lawyer. This page shows Identity KYC, Bar Council, Profile,
 * Documents, Bank, warnings, notes, and audit — in one place — and
 * exposes exactly TWO decision actions (Approve + Reject).
 *
 * Backing endpoints (`routes/admin_lawyer_review.py`):
 *   GET  /api/admin/lawyers/{lid}/review
 *   POST /api/admin/lawyers/{lid}/approve
 *   POST /api/admin/lawyers/{lid}/reject
 *   POST /api/admin/lawyers/{lid}/reviewer-notes
 */
export default function AdminLawyerReview() {
  const { lid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const REJECT_REASONS = [
    "Bar Council certificate is unreadable — please resubmit a clearer scan.",
    "Bar Council number could not be verified with the State Bar Council.",
    "Aadhaar / PAN details do not match the applicant.",
    "Profile bio is too short or generic — please expand.",
    "Missing profile photo — please upload a professional photo.",
    "Bank account details are incorrect or unverifiable.",
    "Duplicate profile detected — please contact support.",
    "Custom reason (see below)",
  ];
  const [rejectForm, setRejectForm] = useState({ reason: "", note: "", scope: "profile" });
  const [noteText, setNoteText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/lawyers/${lid}/review`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load review");
    } finally {
      setLoading(false);
    }
  }, [lid]);
  useEffect(() => { load(); }, [load]);

  const onApprove = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/lawyers/${lid}/approve`, {});
      toast.success("Lawyer approved");
      setApproveOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    } finally {
      setBusy(false);
    }
  };

  const onReject = async () => {
    if (!rejectForm.reason.trim()) {
      toast.error("A rejection reason is required");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post(`/admin/lawyers/${lid}/reject`, rejectForm);
      toast.success("Rejection recorded — lawyer notified.");
      setRejectOpen(false);
      setRejectForm({ reason: "", note: "", scope: "profile" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rejection failed");
    } finally {
      setBusy(false);
    }
  };

  const onAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await adminApi.post(`/admin/lawyers/${lid}/reviewer-notes`, { text: noteText });
      toast.success("Note added");
      setNoteText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add note");
    }
  };

  if (loading || !data) {
    return (
      <div className="min-h-[400px] flex items-center justify-center" data-testid="lawyer-review-loading">
        <Loader2 size={20} className="animate-spin text-slate-300" />
      </div>
    );
  }

  const s = data.current_state;
  const alreadyDecided = s.verification_status === "approved" || s.verification_status === "rejected";

  return (
    <div className="max-w-6xl mx-auto pb-32" data-testid="lawyer-review">
      <Link
        to="/admin-portal/lawyers"
        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0A2540]"
        data-testid="review-back"
      >
        <ArrowLeft size={12} /> Back to lawyers list
      </Link>

      {/* Header */}
      <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          {data.personal.photo ? (
            <img src={photoSrc(data.personal.photo, "md")} alt={data.personal.name} className="h-16 w-16 rounded-2xl object-cover border" />
          ) : (
            <div className="h-16 w-16 rounded-2xl bg-[#0A2540] text-[#D4AF37] grid place-items-center font-bold text-lg">
              {(data.personal.name || "L").split(" ").map(x => x[0] || "").slice(0, 2).join("")}
            </div>
          )}
          <div>
            <h1 className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
              {formatLawyerDisplayName(data.personal.name, "Unnamed lawyer")}
            </h1>
            <p className="text-sm text-slate-500">{data.personal.email} · {data.personal.mobile}</p>
            <p className="text-xs text-slate-400 mt-1">Lawyer ID: {data.lawyer_id}</p>
          </div>
        </div>
        <VerificationPill state={s} />
      </div>

      {/* Section 1: Canonical verification matrix (10 requirements) */}
      <Section title="Verification progress" icon={ShieldCheck} testid="section-progress">
        <VerificationMatrix v={data.verification} />
      </Section>

      {/* Section 2: Personal information */}
      <Section title="Personal information" icon={User} testid="section-personal">
        <Grid>
          <F k="Full name" v={data.personal.name} />
          <F k="Gender" v={data.personal.gender} />
          <F k="DOB" v={data.personal.dob} />
          <F k="Mobile" v={data.personal.mobile} />
          <F k="Email" v={data.personal.email} />
          <F k="Address" v={data.personal.address} />
          <F k="City" v={data.personal.city} />
          <F k="State" v={data.personal.state} />
          <F k="PIN" v={data.personal.pin_code} />
        </Grid>
      </Section>

      {/* Section 3: Identity verification */}
      <Section title="Identity verification (eKYC)" icon={ShieldCheck} testid="section-identity">
        <SubCard label="Aadhaar" status={data.identity.aadhaar.status}>
          <Grid>
            <F k="Aadhaar (masked)" v={data.identity.aadhaar.aadhaar_masked} />
            <F k="Name on record" v={data.identity.aadhaar.name} />
            <F k="DOB" v={data.identity.aadhaar.dob} />
            <F k="Address" v={data.identity.aadhaar.address} />
            <F k="Verified at" v={fmtDate(data.identity.aadhaar.verified_at)} />
          </Grid>
        </SubCard>
        <SubCard label="PAN" status={data.identity.pan.status}>
          <Grid>
            <F k="PAN (masked)" v={data.identity.pan.pan_masked} />
            <F k="Name on record" v={data.identity.pan.name} />
            <F k="Verified at" v={fmtDate(data.identity.pan.verified_at)} />
          </Grid>
        </SubCard>
        <SubCard label="Mobile OTP" status={data.identity.mobile_otp.verified ? "verified" : "not_started"}>
          <p className="text-xs text-slate-500">{fmtDate(data.identity.mobile_otp.verified_at) || "—"}</p>
        </SubCard>
        <SubCard label="Email verification" status={data.identity.email_verified.verified ? "verified" : "not_started"}>
          <p className="text-xs text-slate-500">{fmtDate(data.identity.email_verified.verified_at) || "—"}</p>
        </SubCard>
        <SubCard label="Face match (planned)" status="not_started" muted>Coming soon.</SubCard>
        <SubCard label="DigiLocker" status={data.identity.digilocker.status} muted={data.identity.digilocker.status === "not_started"}>
          {(data.identity.digilocker.documents || []).length === 0 ? (
            <p className="text-xs text-slate-500" data-testid="digilocker-none">No documents fetched from DigiLocker yet.</p>
          ) : (
            <div className="space-y-2" data-testid="digilocker-docs">
              {data.identity.digilocker.documents.map((d) => (
                <div key={d.doc_id} className="rounded-lg border border-slate-200 p-2" data-testid={`digilocker-doc-${d.doc_type}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-bold text-[#0A2540]">{{ aadhaar: "Aadhaar", pan: "PAN" }[d.doc_type] || d.doc_type}</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#0A2540] text-[#D4AF37]">DigiLocker</span>
                    <StatusPill status={d.status} />
                  </div>
                  <Grid>
                    <F k="Name on document" v={d.name} />
                    <F k="Issuer" v={d.issuer} />
                    <F k="DOB" v={d.summary?.dob} />
                    <F k="Masked number" v={d.summary?.masked_id} />
                    <F k="Fetched at" v={fmtDate(d.fetched_at)} />
                  </Grid>
                  {d.name_match_status === "mismatch" && (
                    <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs" role="alert" data-testid={`digilocker-name-mismatch-${d.doc_type}`}>
                      <p className="font-bold">⚠ Name mismatch</p>
                      <p>Registered name: <strong>{d.registered_name || "—"}</strong> · DigiLocker name: <strong>{d.name || "—"}</strong></p>
                      <p>Please verify before approving this document.</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </SubCard>
        <SubCard label="Video KYC (planned)" status="not_started" muted>Coming soon.</SubCard>
      </Section>

      {/* Section 4: Bar Council */}
      <Section title="Bar Council verification" icon={Scale} testid="section-bar-council">
        <div className="flex items-center gap-2 mb-3">
          <StatusPill status={data.bar_council.status} />
          {data.bar_council.verified_at && (
            <span className="text-xs text-slate-500">
              Verified on {new Date(data.bar_council.verified_at).toLocaleString()}
            </span>
          )}
        </div>
        <Grid>
          <F k="Enrolment number" v={data.bar_council.enrolment_number} />
          <F k="State Bar Council" v={data.bar_council.state_bar_council} />
          <F k="Enrolment date" v={data.bar_council.enrolment_date} />
          <F k="Practice since" v={data.bar_council.practice_since} />
          <F k="Submitted at" v={fmtDate(data.bar_council.submitted_at)} />
          <F k="Reviewed at" v={fmtDate(data.bar_council.reviewed_at)} />
        </Grid>
        {data.bar_council.certificate_url && (
          <a
            href={data.bar_council.certificate_url}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 mt-3 text-sm font-bold text-[#0A2540] underline"
            data-testid="bar-council-cert-link"
          >
            <FileText size={14} /> View Bar Council certificate
          </a>
        )}
        {data.bar_council.rejection_reason && (
          <div className="mt-3 rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800">
            <strong>Rejection reason:</strong> {data.bar_council.rejection_reason}
          </div>
        )}
      </Section>

      {/* Section 5: Profile */}
      <Section title="Pricing level" icon={Layers} testid="section-pricing-level">
        <PricingLevelCard lawyerId={lid} level={data.pricing_level} pending={data.pending_level_application} onChanged={load} />
      </Section>

      <Section title="Lawyer profile" icon={BadgeCheck} testid="section-profile">
        <Grid>
          <F k="Specialization" v={data.profile.specialization} />
          <F k="Practice areas" v={(data.profile.practice_areas || []).join(", ") || null} />
          <F k="Languages" v={(data.profile.languages || []).join(", ") || null} />
          <F k="Experience (yrs)" v={data.profile.experience_years} />
          <F k="Education" v={data.profile.education} />
          <F k="Court experience" v={data.profile.court_experience} />
          <F k="Office address" v={data.profile.office_address} />
          <F k="Consultation fees (from level)" v={data.profile.consultation_fees?.source === "level"
            ? `Level ${data.profile.consultation_fees.level} · base ₹${Number(data.profile.consultation_fees.base_fee_rupees).toLocaleString("en-IN")} · client: chat ₹${data.profile.consultation_fees.chat} / audio ₹${data.profile.consultation_fees.audio} / video ₹${data.profile.consultation_fees.video} (15 min)`
            : "No level assigned — fees unavailable"} />
          <F k="Consultation types" v={(data.profile.consultation_types || []).join(", ") || null} />
        </Grid>
        {data.profile.bio && (
          <div className="mt-4">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Bio</p>
            <p className="text-sm text-slate-700 whitespace-pre-line mt-1">{data.profile.bio}</p>
          </div>
        )}
      </Section>

      {/* Public Profile Preview — exactly what clients will see */}
      <Section title="Public profile preview" icon={Eye} testid="section-public-preview">
        <p className="text-xs text-slate-500 mb-4">This is exactly what a client sees when browsing lawyers.</p>
        <PublicProfilePreview data={data} />
      </Section>

      {/* Section 6: Documents */}
      <Section title="Documents" icon={FileText} testid="section-documents">
        {data.documents.length === 0 ? (
          <p className="text-sm text-slate-500">No documents uploaded yet.</p>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {data.documents.map((doc, i) => (
              <li key={doc.url || `${doc.kind}-${i}`}
                  className="flex items-center justify-between border border-slate-200 rounded-xl px-3 py-2"
                  data-testid={`document-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={16} className="text-slate-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#0A2540] truncate">{doc.kind || "Document"}</p>
                    <p className="text-[11px] text-slate-400 truncate">{doc.filename || doc.url}</p>
                  </div>
                </div>
                {doc.url && (
                  <div className="flex items-center gap-2 shrink-0">
                    <a href={doc.url} target="_blank" rel="noreferrer" className="text-xs font-bold text-[#0A2540] underline">View</a>
                    <a href={doc.url} download className="text-xs font-bold text-slate-500 underline">Download</a>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Section 7: Bank */}
      <Section title="Bank details" icon={Wallet} testid="section-bank">
        <div className="flex items-center gap-2 mb-3">
          <StatusPill status={data.bank.status} />
        </div>
        <Grid>
          <F k="Account holder" v={data.bank.account_holder} />
          <F k="Bank name" v={data.bank.bank_name} />
          <F k="Account number (masked)" v={data.bank.account_masked} />
          <F k="IFSC" v={data.bank.ifsc} />
          <F k="UPI ID" v={data.bank.upi_id} />
        </Grid>
      </Section>

      {/* Section 8: Warnings — split blocking vs advisory */}
      <Section title="System validation" icon={AlertTriangle} testid="section-warnings">
        <WarningList
          title="Blocking issues"
          items={data.verdict?.blocking_issues || []}
          emptyText="✓ No blocking issues."
          tone="blocking"
        />
        <div className="mt-4">
          <WarningList
            title="Advisory (nice to fix)"
            items={data.verdict?.advisory_warnings || []}
            emptyText="✓ Nothing advisory to flag."
            tone="advisory"
          />
        </div>
      </Section>

      {/* Section 9: Reviewer notes */}
      <Section title="Reviewer notes (internal)" icon={Sticker} testid="section-notes">
        <div className="flex items-start gap-2">
          <Textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="e.g. Called applicant; awaiting clearer Bar Council certificate."
            className="flex-1"
            rows={2}
            data-testid="reviewer-note-input"
          />
          <Button
            onClick={onAddNote}
            className="rounded-xl adv-btn-primary font-bold"
            data-testid="reviewer-note-add"
          >Add note</Button>
        </div>
        {data.reviewer_notes.length > 0 && (
          <ul className="mt-4 space-y-2" data-testid="reviewer-note-list">
            {data.reviewer_notes.slice().reverse().map((n) => (
              <li key={n.id} className="text-sm border-l-2 border-[#D4AF37] pl-3">
                <p className="text-slate-700">{n.text}</p>
                <p className="text-[10px] text-slate-400">
                  {n.author?.email || n.author?.id} · {fmtDate(n.created_at)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Section 10: Audit */}
      <Section title="Audit history" icon={Clock} testid="section-audit">
        {data.audit.length === 0 ? (
          <p className="text-sm text-slate-500">No audit entries yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {data.audit.map((a, i) => (
              <li key={a.id || i} className="text-sm flex items-start gap-2 py-1"
                  data-testid={`audit-${i}`}>
                <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold w-32 shrink-0 pt-0.5">
                  {fmtDate(a.created_at || a.timestamp)}
                </span>
                <span className="font-bold text-[#0A2540] shrink-0">{a.action}</span>
                <span className="text-slate-500 truncate">
                  {a.actor?.email || a.actor?.id}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Sticky decision panel */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-40" data-testid="decision-panel">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm">
            <p className="font-bold text-[#0A2540]">Decision</p>
            <p className="text-xs text-slate-500">
              {alreadyDecided
                ? `Status: ${s.verification_status}. You can still resubmit changes below.`
                : "One approval decision covers KYC, Bar Council, Profile, and Bank."}
            </p>
          </div>
          <p className="text-xs text-slate-500 max-w-sm" data-testid="review-informational-note">
            Verification is automatic once all onboarding requirements are met. Listing is approved from
            Lawyers → Approve Listing; Verify/Unverify there is an exceptional override.
          </p>
        </div>
      </div>

      {/* Approve confirmation modal */}
      {approveOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="approve-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-5">
            <h2 className="font-extrabold text-[#0A2540] flex items-center gap-2">
              <CheckCircle2 size={18} className="text-emerald-600" /> Approve lawyer
            </h2>
            <p className="text-sm text-slate-700 mt-2">You are approving <strong>{formatLawyerDisplayName(data.personal.name)}</strong>.</p>
            <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 p-3">
              <p className="text-xs font-bold uppercase tracking-widest text-emerald-800 mb-2">Effects</p>
              <ul className="text-sm text-emerald-900 space-y-1">
                <li>✓ Verified Advocate badge enabled</li>
                <li>✓ Searchable across the platform</li>
                <li>✓ Bookable by clients</li>
                <li>✓ Can receive consultations</li>
                <li>✓ Can request payouts</li>
              </ul>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setApproveOpen(false)} data-testid="approve-cancel">Cancel</Button>
              <Button onClick={onApprove}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                      data-testid="approve-confirm" disabled={busy}>
                {busy ? <Loader2 size={16} className="animate-spin mr-1" /> : null} Approve
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejectOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="reject-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-5">
            <h2 className="font-extrabold text-[#0A2540]">Reject lawyer</h2>
            <p className="text-xs text-slate-500 mt-1">The lawyer will be notified and can edit and resubmit.</p>
            <label className="block mt-3 text-xs font-bold text-[#0A2540]">Rejection scope</label>
            <select
              value={rejectForm.scope}
              onChange={(e) => setRejectForm({ ...rejectForm, scope: e.target.value })}
              className="w-full mt-1 rounded-md border border-slate-200 px-3 py-2 text-sm"
              data-testid="reject-scope"
            >
              <option value="profile">Profile / general</option>
              <option value="bar_council">Bar Council</option>
              <option value="identity">Identity KYC</option>
              <option value="bank">Bank</option>
              <option value="documents">Documents</option>
            </select>
            <label className="block mt-3 text-xs font-bold text-[#0A2540]">Reason (shown to lawyer)</label>
            <select
              value={REJECT_REASONS.includes(rejectForm.reason) ? rejectForm.reason : (rejectForm.reason ? "Custom reason (see below)" : "")}
              onChange={(e) => {
                const v = e.target.value;
                setRejectForm({ ...rejectForm, reason: v === "Custom reason (see below)" ? "" : v });
              }}
              className="w-full mt-1 rounded-md border border-slate-200 px-3 py-2 text-sm"
              data-testid="reject-reason-preset"
            >
              <option value="">Pick a common reason…</option>
              {REJECT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <Textarea
              value={rejectForm.reason}
              onChange={(e) => setRejectForm({ ...rejectForm, reason: e.target.value })}
              rows={3}
              className="mt-2"
              placeholder="Type the reason (or refine the preset above)"
              data-testid="reject-reason"
            />
            <p className="mt-2 text-[11px] text-slate-500">
              The lawyer will only be able to edit and resubmit the <strong>{rejectForm.scope}</strong> section.
            </p>
            <label className="block mt-3 text-xs font-bold text-[#0A2540]">Internal note (optional)</label>
            <Input
              value={rejectForm.note}
              onChange={(e) => setRejectForm({ ...rejectForm, note: e.target.value })}
              placeholder="Only visible to admins" data-testid="reject-note"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setRejectOpen(false)} data-testid="reject-cancel">Cancel</Button>
              <Button
                onClick={onReject}
                className="bg-rose-600 hover:bg-rose-700 text-white font-bold"
                data-testid="reject-confirm"
                disabled={busy}
              >{busy ? <Loader2 size={16} className="animate-spin mr-1" /> : null} Confirm rejection</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- small building blocks ---------- */

const Section = ({ title, icon: Icon, testid, children }) => (
  <section className="mt-8 bg-white border border-slate-200 rounded-2xl p-5" data-testid={testid} id={testid}>
    <div className="flex items-center gap-2 mb-4">
      <Icon size={16} className="text-[#0A2540]" />
      <h2 className="font-bold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>{title}</h2>
    </div>
    {children}
  </section>
);

const Grid = ({ children }) => (
  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">{children}</dl>
);

// Never hand React a raw object/array: flatten to text so one odd field can't crash the page.
const asText = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (Array.isArray(v)) return v.map(asText).filter(Boolean).join(", ") || null;
  if (typeof v === "object") return Object.values(v).map(asText).filter(Boolean).join(", ") || null;
  return String(v);
};

const F = ({ k, v }) => (
  <div>
    <dt className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{k}</dt>
    <dd className="text-[#0A2540] font-medium mt-0.5">{asText(v) ?? <span className="text-slate-300">—</span>}</dd>
  </div>
);

const SubCard = ({ label, status, muted, children }) => (
  <div className={"mt-3 first:mt-0 rounded-xl border p-3 " + (muted ? "bg-slate-50 border-slate-200 opacity-70" : "bg-white border-slate-200")}>
    <div className="flex items-center gap-2 mb-2">
      <span className="text-sm font-bold text-[#0A2540]">{label}</span>
      <StatusPill status={status} />
    </div>
    {children}
  </div>
);

const STATUS_META = {
  verified:    { label: "Verified",    cls: "bg-emerald-100 text-emerald-800" },
  approved:    { label: "Approved",    cls: "bg-emerald-100 text-emerald-800" },
  submitted:   { label: "Submitted",   cls: "bg-amber-100 text-amber-800" },
  under_review:{ label: "Under review",cls: "bg-amber-100 text-amber-800" },
  pending:     { label: "Pending",     cls: "bg-amber-100 text-amber-800" },
  pending_review: { label: "Under review", cls: "bg-amber-100 text-amber-800" },
  in_review:   { label: "In review",   cls: "bg-amber-100 text-amber-800" },
  manual_review:{ label: "Manual review", cls: "bg-amber-100 text-amber-800" },
  not_started: { label: "Not started", cls: "bg-slate-100 text-slate-600" },
  rejected:    { label: "Rejected",    cls: "bg-rose-600 text-white" },
};

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || { label: status || "—", cls: "bg-slate-100 text-slate-600" };
  return (
    <span
      className={"inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider " + m.cls}
      data-testid={`status-pill-${status || 'unknown'}`}
    >{m.label}</span>
  );
};

const VerificationPill = ({ state }) => {
  const cls =
    state.verification_status === "approved" ? "bg-emerald-100 text-emerald-800"
    : state.verification_status === "rejected" ? "bg-rose-600 text-white"
    : "bg-amber-100 text-amber-800";
  return (
    <span className={"inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider " + cls}
          data-testid="review-verification-pill">
      {state.verification_status?.replace("_", " ") || "in review"}
    </span>
  );
};

const fmtDate = (v) => (v ? new Date(v).toLocaleString() : null);



/* ---------- Public Profile Preview ---------- */

function PublicProfilePreview({ data }) {
  const { personal, profile, bar_council } = data;
  const rupees = (v) => {
    if (v == null || v === "") return null;
    if (typeof v === "object") return JSON.stringify(v);
    return `₹${Number(v).toLocaleString("en-IN")}`;
  };
  const isVerified = bar_council.status === "verified";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="public-profile-preview-card">
      <div className="flex items-start gap-4 flex-wrap">
        {profile.photo ? (
          <img src={photoSrc(profile.photo, "md")} alt={personal.name} className="h-24 w-24 rounded-2xl object-cover border" />
        ) : (
          <div className="h-24 w-24 rounded-2xl bg-[#0A2540] text-[#D4AF37] grid place-items-center font-bold text-2xl">
            {(personal.name || "L").split(" ").map(x => x[0] || "").slice(0, 2).join("")}
          </div>
        )}
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
              {formatLawyerDisplayName(personal.name, "Unnamed")}
            </h3>
            {isVerified && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-[#0A2540] text-[#D4AF37]"
                data-testid="preview-verified-badge"
              >
                <BadgeCheck size={13} strokeWidth={2.5} /> Verified Advocate
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {profile.specialization || "—"}{profile.experience_years ? ` · ${profile.experience_years}y experience` : ""}
            {personal.city ? ` · ${personal.city}` : ""}
          </p>
          <div className="mt-2 flex items-center gap-3 text-xs text-slate-600 flex-wrap">
            <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 px-2 py-0.5 rounded-md font-bold">
              <Star size={11} fill="currentColor" /> — · No reviews yet
            </span>
            {profile.consultation_fees?.source === "level" ? (
              <span className="font-bold text-[#0A2540]" data-testid="preview-fee">
                {rupees(profile.consultation_fees.video)}
                <span className="text-slate-400 font-normal"> / video · 15 min (Level {profile.consultation_fees.level})</span>
              </span>
            ) : (
              <span className="text-slate-400 italic" data-testid="preview-fee">Fee shown once a level is assigned</span>
            )}
          </div>
          {(profile.languages || []).length > 0 && (
            <p className="mt-2 text-xs text-slate-500">
              <strong>Languages:</strong> {(profile.languages || []).join(", ")}
            </p>
          )}
          {(profile.practice_areas || []).length > 0 && (
            <p className="mt-1 text-xs text-slate-500">
              <strong>Practice areas:</strong> {(profile.practice_areas || []).join(", ")}
            </p>
          )}
        </div>
      </div>
      {profile.bio && (
        <div className="mt-4">
          <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">About</p>
          <p className="text-sm text-slate-700 whitespace-pre-line mt-1">{profile.bio}</p>
        </div>
      )}
      {!profile.bio && (
        <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
          ⚠ No bio yet — clients skip lawyers without a bio.
        </div>
      )}
    </div>
  );
}


/* ---------- Warning list (blocking vs advisory) ---------- */

function WarningList({ title, items, emptyText, tone }) {
  const isBlocking = tone === "blocking";
  return (
    <div data-testid={`warnings-${tone}`}>
      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">{title}</p>
      {items.length === 0 ? (
        <p className="text-sm text-emerald-700">{emptyText}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((w) => (
            <li key={w.code}
                className={
                  "rounded-xl border p-3 text-sm " +
                  (isBlocking
                    ? "bg-rose-50 border-rose-200 text-rose-900"
                    : "bg-amber-50 border-amber-200 text-amber-900")
                }
                data-testid={`warning-${w.code}`}>
              <strong className="uppercase text-[10px] tracking-wider">
                {isBlocking ? "Blocking" : "Advisory"}
              </strong> — {w.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
